import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, LOCALE } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { SecurityLink, PrivateMode } from "@/components/privacy/PrivateMode";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RightsRequests } from "@/components/privacy/RightsRequests";
import { DeleteAccount, DownloadData } from "@/components/privacy/DataControls";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AGE_LABEL = { adult: "18 or over", minor: "Under 18 — safer settings on", unknown: "Not recorded", under_min: "Below minimum age" };

const WhereAndAge = ({ d, onSaved }) => {
  const [j, setJ] = useState(d.jurisdiction);
  const save = async () => { try { await api.put("/privacy/profile", { jurisdiction: j }); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="surface space-y-3 p-5 sm:p-6"><h2 className="text-xl">Where you are & your age</h2>
      <p className="text-xs text-slate-500">Where you are decides which privacy rules and defaults apply. We don't rely only on your IP address.</p>
      <div className="flex flex-wrap gap-2">
        <Select value={j} onValueChange={setJ}><SelectTrigger className="w-64" data-testid="privacy-jurisdiction"><SelectValue /></SelectTrigger>
          <SelectContent>{d.jurisdictions.map((x) => <SelectItem key={x.code} value={x.code}>{x.name}</SelectItem>)}</SelectContent></Select>
        <Button variant="outline" className="rounded-full" data-testid="privacy-profile-save" onClick={save}>Save</Button>
      </div>
      <p className="text-sm" data-testid="privacy-status">Rules: <b>{d.jurisdiction_name}</b> · Age: <b data-testid="privacy-age-class">{AGE_LABEL[d.age_class] || d.age_class}</b> · Notice version {d.notice_version}</p>
      <p className="text-xs text-slate-500" data-testid="privacy-dob">Date of birth: {d.date_of_birth ? new Date(d.date_of_birth).toLocaleDateString(LOCALE) : "not recorded"} ({d.age_assurance || "self-declared"}). To fix a mistake, send a correction request below.</p>
      {d.age_class === "minor" && <p className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-3 text-xs text-emerald-900" data-testid="minor-protections">Because you're under 18: personalised commercial content and marketing are switched off and can't be turned on. Recommendations and location start off. Your posts and friend list default to friends only, and only friends can message you.</p>}
    </section>
  );
};

const Purpose = ({ p, onSet }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`purpose-${p.key}`}>
    <div className="min-w-0 flex-1"><p className="text-sm font-medium">{p.label}</p><p className="text-xs text-slate-500">{p.notice}</p></div>
    {!p.optional ? <span className="text-xs text-slate-500">Always on (needed)</span> : p.locked ? <span className="text-xs text-slate-500" data-testid={`purpose-${p.key}-locked`}>Off — not available for your age</span> : (
      <div className="flex gap-1" role="radiogroup" aria-label={p.label}>
        {[[true, "On"], [false, "Off"]].map(([v, l]) => (
          <button key={l} role="radio" aria-checked={p.enabled === v} data-testid={`purpose-${p.key}-${l.toLowerCase()}`} onClick={() => onSet(p.key, v)}
            className={`w-14 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${p.enabled === v ? "border-slate-900 bg-slate-900 text-white" : "border-border bg-white text-slate-700 hover:border-slate-400"}`}>{l}</button>))}
      </div>)}
  </div>
);

export default function PrivacyCentre() {
  const [d, setD] = useState(null);
  const load = () => api.get("/privacy/me").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner />;
  const set = async (purpose, granted) => { try { await api.post("/privacy/consents", { purpose, granted }); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="privacy-centre">
      <PageHeader title="Privacy Centre" subtitle="Your privacy choices for Middle. Optional uses are separate from each other, and you can change any of them at any time." />
      <Tabs defaultValue="controls">
        <TabsList className="mb-4 w-full sm:w-auto" data-testid="privacy-tabs">
          <TabsTrigger value="controls" className="flex-1 sm:flex-none" data-testid="privacy-tab-controls">Your choices</TabsTrigger>
          <TabsTrigger value="policy" className="flex-1 sm:flex-none" data-testid="privacy-tab-policy">Privacy Policy</TabsTrigger>
        </TabsList>
        <TabsContent value="policy" className="surface p-5 sm:p-6"><PrivacyPolicy region={d.jurisdiction} /></TabsContent>
        <TabsContent value="controls" className="space-y-6">
      <SecurityLink />
      <PrivateMode />
      <WhereAndAge key={d.jurisdiction} d={d} onSaved={load} />
      <section className="surface divide-y divide-border p-5 sm:p-6" data-testid="privacy-purposes"><h2 className="pb-2 text-xl">Your choices</h2>
        {d.purposes.map((p) => <Purpose key={p.key} p={p} onSet={set} />)}
      </section>
      <RightsRequests rights={d.rights} days={d.response_days} />
      <DownloadData />
      <DeleteAccount />
      <section className="surface space-y-2 p-5 sm:p-6"><h2 className="text-xl">Consent history</h2>
        <p className="text-xs text-slate-500">Cookies: Middle uses only the cookies needed to keep you signed in, with no analytics, advertising or third-party tracking cookies. </p>
        {d.history.length === 0 ? <p className="text-sm text-slate-500">No changes yet.</p> : <ul className="space-y-1 text-xs text-slate-600" data-testid="consent-history">{d.history.map((h) => <li key={h.id}>{new Date(h.at).toLocaleString(LOCALE)} — {h.purpose}: <b>{h.status}</b> (notice {h.notice_version}, {h.jurisdiction})</li>)}</ul>}
      </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
